import { apiGroup, menuItem, product } from "../../../utils/constants";

export type MenuItem = {
  title: string;
  color?: string;
  items?: MenuItem[];
  path?: string;
};

type MethodType = 'GET' | 'POST' | 'PUT' | 'DEL';

enum MenuGroup {
  GetStarted = "Get Started",
  RESTApi = "REST APIs",
  Webhooks = "Webhooks"
}

const createItem = (title: string, product: string, menuGroup: MenuGroup, method?: MethodType, apiGroup?: string): MenuItem => ({
  title: method ? `${method} ${title}` : title,
  color: method ? {
    GET: '#355ABF',
    POST: '#1F8747',
    PUT: '#D36D13',
    DEL: '#AE1B6A',
  }[method] : '',
  path: apiGroup ? `${menuGroup}/${product}/${apiGroup}` : `${menuGroup}/${product}`
});

const authenticationMenu: MenuItem = {
  title: 'Authentication',
  items: [
    createItem('Overview', 'Authentication', MenuGroup.RESTApi, undefined, undefined),
    createItem('Login', 'Authentication', MenuGroup.RESTApi, 'POST', apiGroup.login),
    createItem('Refresh Access Token', 'Authentication', MenuGroup.RESTApi, 'POST', apiGroup.login),
  ],
};

const apiGuidesMenu: MenuItem = {
  title: 'Guides',
  items: [
    createItem('Overview', product.Guides, MenuGroup.RESTApi, undefined, undefined),
    createItem('Errors', product.Guides, MenuGroup.RESTApi, undefined, undefined),
    createItem('Rate Limiting', product.Guides, MenuGroup.RESTApi, undefined, undefined),
  ],
}

const SafeSendSuiteMenu: MenuItem = {
  title: 'SafeSend Suite',
  items: [
    {
      title: 'Client Management',
      items: [
        createItem('All', product.SafeSend_Suite, MenuGroup.RESTApi, 'GET', apiGroup.Client_Management),
        createItem('Add Client', product.SafeSend_Suite, MenuGroup.RESTApi, 'POST', apiGroup.Client_Management),
        createItem('Upload client CSV', product.SafeSend_Suite, MenuGroup.RESTApi, 'POST', apiGroup.Client_Management),
        createItem('Edit Client', product.SafeSend_Suite, MenuGroup.RESTApi, 'PUT', apiGroup.Client_Management),
        createItem('Delete Client', product.SafeSend_Suite, MenuGroup.RESTApi, 'DEL', apiGroup.Client_Management),
      ],
    },
    {
      title: 'Office Location',
      items: [
        createItem('All', product.SafeSend_Suite, MenuGroup.RESTApi, 'GET', apiGroup.office_location),
        createItem('Location By Id', product.SafeSend_Suite, MenuGroup.RESTApi, 'GET', apiGroup.office_location),
      ],
    },
    {
      title: 'User Management',
      items: [
        createItem('All', product.SafeSend_Suite, MenuGroup.RESTApi, 'GET', apiGroup.user_management),
        createItem('All User Groups', product.SafeSend_Suite, MenuGroup.RESTApi, 'GET', apiGroup.user_management),
        createItem('Add User', product.SafeSend_Suite, MenuGroup.RESTApi, 'POST', apiGroup.user_management),
        createItem('Edit User', product.SafeSend_Suite, MenuGroup.RESTApi, 'PUT', apiGroup.user_management),
        createItem('Delete User', product.SafeSend_Suite, MenuGroup.RESTApi, 'DEL', apiGroup.user_management),
      ],
    },
  ],
}

const safeSendReturnsMenu: MenuItem = {
  title: 'SafeSend Returns',
  items: [
    {
      title: 'Archived',
      items: [
        createItem('All', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Archived),
        createItem('Access Link', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Archived),
        createItem('Signature Status', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Archived),
        createItem('Signer Information', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Archived),
        createItem('Resend Access Link', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Archived),
        createItem('Send Reminder', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Archived),
        createItem('Move To Delivered', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.Archived),
        createItem('Recall Return', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.Archived),
        createItem('Delete Return', product.SafeSend_Returns, MenuGroup.RESTApi, 'DEL', apiGroup.Archived),
      ],
    },
    {
      title: 'Delivered',
      items: [
        createItem('All', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Delivered),
        createItem('Signature Status', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Delivered),
        createItem('Signer Info', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Delivered),
        createItem('Access Link', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Delivered),
        createItem('Efile Forms', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Delivered),
        createItem('Send Reminder', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Delivered),
        createItem('Resend Access Link', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Delivered),
        createItem('Archive', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.Delivered),
        createItem('Recall', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.Delivered),
        createItem('Delivered Return', product.SafeSend_Returns, MenuGroup.RESTApi, 'DEL', apiGroup.Delivered),
      ],
    },
    {
      title: 'In Process',
      items: [
        createItem('All', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.In_Process),
        createItem('Change Status', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.In_Process),
        createItem('Assign', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.In_Process),
        createItem('In Process Return', product.SafeSend_Returns, MenuGroup.RESTApi, 'DEL', apiGroup.In_Process),
      ],
    },
    {
      title: 'Recycle Bin',
      items: [
        createItem('All', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Recycle_Bin),
        createItem('Restore', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.Recycle_Bin),
        createItem('Recycle Bin Return', product.SafeSend_Returns, MenuGroup.RESTApi, 'DEL', apiGroup.Recycle_Bin),
      ],
    },
    {
      title: 'Returns',
      items: [
        createItem('Uploaded Returns', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Returns),
        createItem('Unrecognized Returns', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Returns),
        createItem('Fetch Return Details', product.SafeSend_Returns, MenuGroup.RESTApi, 'GET', apiGroup.Returns),
        createItem('Recognize', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Returns),
        createItem('Upload', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Returns),
        createItem('Partial Upload', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Returns),
        createItem('Append', product.SafeSend_Returns, MenuGroup.RESTApi, 'POST', apiGroup.Returns),
        createItem('Set Access', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.Returns),
      ],
    },
    {
      title: 'Return Status',
      items: [
        createItem('Set Download Status', product.SafeSend_Returns, MenuGroup.RESTApi, 'PUT', apiGroup.ReturnStatus),
      ],
    },
  ],
};

const safeSendExchangeMenu: MenuItem = {
  title: 'SafeSend Exchange',
  items: [
    {
      title: 'Compose Message',
      items: [
        createItem('Send Message', product.SafeSend_Exchange, MenuGroup.RESTApi, 'POST', apiGroup.Safesend_Exchange_ComposeMessage),
      ],
    },
    {
      title: 'Document Request',
      items: [
        createItem('Document Request', product.SafeSend_Exchange, MenuGroup.RESTApi, 'POST', apiGroup.Safesend_Exchange_DocumentRequest),
      ],
    },
    {
      title: 'Document Request Template',
      items: [
        createItem('Document Request Template', product.SafeSend_Exchange, MenuGroup.RESTApi, 'GET', apiGroup.Safesend_Exchange_DocumentRequestTemplate),
      ],
    },
  ],
};

const safeSendSignaturesMenu: MenuItem = {
  title: 'SafeSend Signatures',
  items: [
    {
      title: 'Archive Report',
      items: [
        createItem('All', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Archive_Report),
        createItem('Signer Details', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Archive_Report),
        createItem('Access Link', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Archive_Report),
        createItem('Send Reminder', product.SafeSend_Signatures, MenuGroup.RESTApi, 'POST', apiGroup.Archive_Report),
        createItem('Resend Access Link', product.SafeSend_Signatures, MenuGroup.RESTApi, 'POST', apiGroup.Archive_Report),
        createItem('Restore', product.SafeSend_Signatures, MenuGroup.RESTApi, 'PUT', apiGroup.Archive_Report),
        createItem('Archived Signature', product.SafeSend_Signatures, MenuGroup.RESTApi, 'DEL', apiGroup.Archive_Report),
      ],
    },
    {
      title: 'Delivered Report',
      items: [
        createItem('All', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Delivered_Report),
        createItem('Signer Details', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Delivered_Report),
        createItem('Access Link', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Delivered_Report),
        createItem('Send Reminder', product.SafeSend_Signatures, MenuGroup.RESTApi, 'POST', apiGroup.Delivered_Report),
        createItem('Resend Access Link', product.SafeSend_Signatures, MenuGroup.RESTApi, 'POST', apiGroup.Delivered_Report),
        createItem('Archive Signature', product.SafeSend_Signatures, MenuGroup.RESTApi, 'PUT', apiGroup.Delivered_Report),
        createItem('Delete Signature', product.SafeSend_Signatures, MenuGroup.RESTApi, 'DEL', apiGroup.Delivered_Report),
      ],
    },
    {
      title: 'Draft Report',
      items: [
        createItem('All', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Draft_Report),
        createItem('Draft Signature', product.SafeSend_Signatures, MenuGroup.RESTApi, 'DEL', apiGroup.Draft_Report),
      ],
    },
    {
      title: 'Recycle Bin Report',
      items: [
        createItem('All', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Recycle_Bin_report),
        createItem('Restore Recycle Bin', product.SafeSend_Signatures, MenuGroup.RESTApi, 'PUT', apiGroup.Recycle_Bin_report),
        createItem('Recycle Bin', product.SafeSend_Signatures, MenuGroup.RESTApi, 'DEL', apiGroup.Recycle_Bin_report),
      ],
    },
    {
      title: 'Status',
      items: [
        createItem('Signature Status', product.SafeSend_Signatures, MenuGroup.RESTApi, 'PUT', apiGroup.Status),
      ],
    },
    {
      title: 'Signature Upload',
      items: [
        createItem('Signature Status', product.SafeSend_Signatures, MenuGroup.RESTApi, 'GET', apiGroup.Signature_Upload),
        createItem('Send For Signature', product.SafeSend_Signatures, MenuGroup.RESTApi, 'POST', apiGroup.Signature_Upload),
        createItem('Send For Signature(With Text Id)', product.SafeSend_Signatures, MenuGroup.RESTApi, 'POST', apiGroup.Signature_Upload),
        createItem('Set Access', product.SafeSend_Signatures, MenuGroup.RESTApi, 'PUT', apiGroup.Signature_Upload),
      ],
    },
  ],
};

const safeSendOrganizersMenu: MenuItem = {
  title: 'SafeSend Organizers',
  items: [
    {
      title: 'Archived Organizer',
      items: [
        createItem('Archived Organizers', product.SafeSend_Organizer, MenuGroup.RESTApi, 'GET', apiGroup.Organizer_Archived),
        createItem('Signer Details', product.SafeSend_Organizer, MenuGroup.RESTApi, 'GET', apiGroup.Organizer_Archived),
        createItem('Resend Access Link', product.SafeSend_Organizer, MenuGroup.RESTApi, 'POST', apiGroup.Organizer_Archived),
        createItem('Move To Delivered', product.SafeSend_Organizer, MenuGroup.RESTApi, 'PUT', apiGroup.Organizer_Archived),
        createItem('Delete Organizer API', product.SafeSend_Organizer, MenuGroup.RESTApi, 'DEL', apiGroup.Organizer_Archived),
      ],
    },
    {
      title: 'Delivered Organizer',
      items: [
        createItem('Signer Details', product.SafeSend_Organizer, MenuGroup.RESTApi, 'GET', apiGroup.Organizer_Delivered),
        createItem('Delivered Organizers', product.SafeSend_Organizer, MenuGroup.RESTApi, 'GET', apiGroup.Organizer_Delivered),
        createItem('Send Reminder API', product.SafeSend_Organizer, MenuGroup.RESTApi, 'POST', apiGroup.Organizer_Delivered),
        createItem('Resend Access Link', product.SafeSend_Organizer, MenuGroup.RESTApi, 'POST', apiGroup.Organizer_Delivered),
        createItem('Archive Organizer', product.SafeSend_Organizer, MenuGroup.RESTApi, 'PUT', apiGroup.Organizer_Delivered),
        createItem('Delete Organizer API', product.SafeSend_Organizer, MenuGroup.RESTApi, 'DEL', apiGroup.Organizer_Delivered),
      ],
    },
    {
      title: 'In Process Organizer',
      items: [
        createItem('All', product.SafeSend_Organizer, MenuGroup.RESTApi, 'GET', apiGroup.Organizer_In_Process),
        createItem('Delete In Process Organizer', product.SafeSend_Organizer, MenuGroup.RESTApi, 'DEL', apiGroup.Organizer_In_Process),
      ],
    },
    {
      title: 'Organizer Status',
      items: [
        createItem('Organizer Status', product.SafeSend_Organizer, MenuGroup.RESTApi, 'GET', apiGroup.Organizer_Status),
        createItem('Update Document Status', product.SafeSend_Organizer, MenuGroup.RESTApi, 'PUT', apiGroup.Organizer_Status),
      ],
    },
    {
      title: 'Recycled Organizer',
      items: [
        createItem('All', product.SafeSend_Organizer, MenuGroup.RESTApi, 'GET', apiGroup.Organizer_Recycle_Bin),
        createItem('Restore', product.SafeSend_Organizer, MenuGroup.RESTApi, 'PUT', apiGroup.Organizer_Recycle_Bin),
        createItem('Recycle Bin', product.SafeSend_Organizer, MenuGroup.RESTApi, 'DEL', apiGroup.Organizer_Recycle_Bin),
      ],
    },
    {
      title: 'Upload Organizer',
      items: [
        createItem('Proforma', product.SafeSend_Organizer, MenuGroup.RESTApi, 'POST', apiGroup.Organizer_UploadOrganizer),
        createItem('Batch', product.SafeSend_Organizer, MenuGroup.RESTApi, 'POST', apiGroup.Organizer_UploadOrganizer),
      ],
    },
  ],
};

const safeSendGatherMenu: MenuItem = {
  title: 'SafeSend Gather',
  items: [
    {
      title: 'Delivered Gather', items: [
        createItem('Delivered Gather', product.SafeSend_Gather, MenuGroup.RESTApi, 'DEL', apiGroup.Delivered_Gather),
        createItem('Archive', product.SafeSend_Gather, MenuGroup.RESTApi, 'PUT', apiGroup.Delivered_Gather),
      ]
    },
    {
      title: 'In Process Gather', items: [
        createItem('All', product.SafeSend_Gather, MenuGroup.RESTApi, 'GET', apiGroup.In_Process_Gather),
        createItem('In Process Gather', product.SafeSend_Gather, MenuGroup.RESTApi, 'DEL', apiGroup.In_Process_Gather),
      ]
    },
  ]
}

const restAPIsMenu: MenuItem = {
  title: 'REST APIs',
  items: [
    apiGuidesMenu,
    authenticationMenu,
    SafeSendSuiteMenu,
    safeSendReturnsMenu,
    safeSendExchangeMenu,
    safeSendSignaturesMenu,
    safeSendOrganizersMenu,
    safeSendGatherMenu
  ],
};

const webhookGuidesMenu: MenuItem = {
  title: 'Guides',
  items: [
    createItem('Overview', product.Guides, MenuGroup.Webhooks),
    createItem('Errors', product.Guides, MenuGroup.Webhooks),
  ]
};

const safesendExchangeWebhook: MenuItem = {
  title: 'SafeSend Exchange',
  items: [
    createItem('Download DRL Document', product.SafeSend_Exchange, MenuGroup.Webhooks),
  ]
};
const safesendReturnsWebhook: MenuItem = {
  title: 'SafeSend Returns',
  items: [
    createItem('Download Esign Document', product.SafeSend_Returns, MenuGroup.Webhooks),
    createItem('Return Status Changed', product.SafeSend_Returns, MenuGroup.Webhooks),
  ]
};
const safesendSignatureWebhook: MenuItem = {
  title: 'SafeSend Signatures',
  items: [
    createItem('Document Signed', product.SafeSend_Signatures, MenuGroup.Webhooks),
    createItem('Signature Status Changed', product.SafeSend_Signatures, MenuGroup.Webhooks),
  ]
};
const safesendOrganizersWebhook: MenuItem = {
  title: 'SafeSend Organizers',
  items: [
    createItem('Engagement Letter Signed', product.SafeSend_Organizer, MenuGroup.Webhooks),
    createItem('Organizer Completed', product.SafeSend_Organizer, MenuGroup.Webhooks),
    createItem('Source Document Uploaded', product.SafeSend_Organizer, MenuGroup.Webhooks),
    createItem('Accessed Organizer Notification', product.SafeSend_Organizer, MenuGroup.Webhooks),
    createItem('Questionnaire completed', product.SafeSend_Organizer, MenuGroup.Webhooks),
  ]
};

const SafesendGatherWebhook: MenuItem = {
  title: 'SafeSend Gather',
  items: [
    createItem('Download Esign Documents', product.SafeSend_Gather, MenuGroup.Webhooks),
    createItem('Download Fillable Organizer', product.SafeSend_Gather, MenuGroup.Webhooks),
    createItem('Download Source Documents', product.SafeSend_Gather, MenuGroup.Webhooks),
  ]
};

const clientManagementWebhook: MenuItem = {
  title: 'Client Management',
  items: [
    createItem(menuItem.clientInformationChange, "Client Management", MenuGroup.Webhooks)
  ]
};

const webhooksMenu: MenuItem = {
  title: 'Webhooks',
  items: [
    webhookGuidesMenu,
    safesendExchangeWebhook,
    safesendReturnsWebhook,
    safesendSignatureWebhook,
    safesendOrganizersWebhook,
    SafesendGatherWebhook,
    clientManagementWebhook
  ]
};

export const menuData: MenuItem[] = [
  restAPIsMenu,
  webhooksMenu,
];
